// Integration shapes are generated from OrbitalX.Dto — see api-generated.d.ts.
//
// The three enums stay runtime enums because callers compare against and index by their members.
// Their values are the C# enum NAMES: IntegrationConnectionMapper uses .ToString(), not
// .ToDescription(), so the spaced descriptions on those enums never leave the server.

import type {
  IntegrationConnectionCreateDto as GeneratedIntegrationConnectionCreateDto,
  IntegrationConnectionSummaryDto as GeneratedIntegrationConnectionSummaryDto,
  IntegrationConnectionViewDto as GeneratedIntegrationConnectionViewDto,
  IntegrationConnectionWithKeyDto as GeneratedIntegrationConnectionWithKeyDto,
} from './api-generated'

export type { IntegrationConnectionUpdateDto, IntegrationErrorEntryDto } from './api-generated'

/**
 * A BigMarker channel offered when an online event chooses where its conference is created.
 *
 * ⚑ The C# name is BigMarkerChannelViewDto. `BigMarkerChannelDto` also exists in OrbitalX.Dto and is
 * NOT this: it is the inbound vendor shape, whose [JsonPropertyName] attributes put snake_case on the
 * wire. Returning that one from an endpoint is what made the channel dropdown render blank rows
 * (ORB-672), so the alias here points at the projection, deliberately.
 *
 * `IsDefault` is true for the channel this company's most recent webinar was on — server-computed,
 * not from BigMarker.
 */
export type { BigMarkerChannelViewDto as BigMarkerChannelDto } from './api-generated'

export enum IntegrationProvider {
    Salesforce = 'Salesforce',
    HubSpot = 'HubSpot',
    BigMarker = 'BigMarker',
    Webhook = 'Webhook',
    Dynamics = 'Dynamics',
}

export enum IntegrationConnectionStatus {
    PendingAuthorization = 'PendingAuthorization',
    Active = 'Active',
    Disabled = 'Disabled',
    Error = 'Error',
    AuthExpired = 'AuthExpired',
}

export enum IntegrationAuthType {
    ApiKey = 'ApiKey',
    OAuthAuthorizationCode = 'OAuthAuthorizationCode',
}

/** Response from the GET .../ErrorSummary endpoint — an anonymous object, hence camelCase. */
export interface IntegrationErrorSummaryDto {
    aiSummary: string | null
}

/** Create body. Everything is optional server-side, which is why nothing here is required. */
export interface IntegrationConnectionCreateDto
    extends Omit<GeneratedIntegrationConnectionCreateDto, 'Provider' | 'AuthType'> {
    Provider?: IntegrationProvider | string | null
    AuthType?: IntegrationAuthType | string | null
}

/**
 * What a read returns.
 *
 * ⚑ It carries no secrets. The hand-written version of this type extended the CREATE DTO, so it
 * claimed AccessToken, RefreshToken, ApiKey, SecondarySecret, MappingConfigJson and
 * ProviderSettingsJson were readable — none of them are on the wire. `AiSummary` is the server's
 * cached plain-English error summary; null means the panel must fetch it.
 */
export interface IntegrationConnectionViewDto
    extends Omit<GeneratedIntegrationConnectionViewDto, 'Provider' | 'Status' | 'AuthType'> {
    Provider: IntegrationProvider | string
    Status: IntegrationConnectionStatus | string
    AuthType: IntegrationAuthType | string
}

/**
 * The create and Webhook-reset responses, and the only shape carrying the plaintext key. Shown to the
 * user exactly once via the one-time reveal modal and never persisted in client state beyond it.
 */
export interface IntegrationConnectionWithKeyDto
    extends Omit<GeneratedIntegrationConnectionWithKeyDto, 'Provider' | 'Status' | 'AuthType'> {
    Provider: IntegrationProvider | string
    Status: IntegrationConnectionStatus | string
    AuthType: IntegrationAuthType | string
}

export type IntegrationConnectionListResponse = IntegrationConnectionViewDto[]

/**
 * What a workspace member may read about the company's integrations (ORB-699).
 *
 * Four fields on purpose. The full IntegrationConnectionViewDto is company-admin only and carries
 * error history, sync counters and the AI error summary; this is the projection served by
 * `Workspaces/{workspaceId}/Integrations/Summary`, which any workspace Viewer can reach.
 */
export interface IntegrationConnectionSummaryDto
    extends Omit<GeneratedIntegrationConnectionSummaryDto, 'Provider' | 'Status'> {
    Provider: IntegrationProvider | string
    Status: IntegrationConnectionStatus | string
}

export const INTEGRATION_STATUS_COLORS: Record<string, string> = {
    PendingAuthorization: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    Active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    Disabled: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    AuthExpired: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
}

export const INTEGRATION_AUTH_LABELS: Record<string, string> = {
    ApiKey: 'API Key',
    OAuthAuthorizationCode: 'OAuth',
}

export const INTEGRATION_PROVIDER_LABELS: Record<string, string> = {
    Salesforce: 'Salesforce',
    HubSpot: 'HubSpot',
    BigMarker: 'BigMarker',
    Webhook: 'Webhook',
    Dynamics: 'Dynamics 365',
}
